import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Search, Flame, Star, Plus, Utensils } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";

const CATEGORY_IDS = ["all", "soups", "starters", "specials", "mains", "biryani", "breads", "rice", "desserts", "drinks"];

// Each category — icon glyph + Chinese index
const CAT_META = {
  soups:    { num: "01", glyph: "🥣", zh: "湯品" },
  starters: { num: "02", glyph: "🥟", zh: "前菜・烤爐" },
  specials: { num: "03", glyph: "⭐", zh: "杜爾希精選" },
  mains:    { num: "04", glyph: "🍛", zh: "咖哩主餐" },
  biryani:  { num: "05", glyph: "🍚", zh: "印度香飯" },
  breads:   { num: "06", glyph: "🫓", zh: "印度烤餅" },
  rice:     { num: "07", glyph: "🌾", zh: "白飯" },
  desserts: { num: "08", glyph: "🍮", zh: "甜點" },
  drinks:   { num: "09", glyph: "🥂", zh: "飲品" },
};

export default function MenuPage() {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [vegOnly, setVegOnly] = useState(false);
  const { addItem } = useCart();
  const { t, i18n } = useTranslation();
  const isZh = i18n.language.startsWith("zh");

  useEffect(() => {
    api.get("/menu", { params: { available_only: true } }).then((r) => setItems(r.data));
    // If returning from an edit, rehydrate the cart with original items
    if (localStorage.getItem("tulsi_editing_order")) {
      try {
        const raw = sessionStorage.getItem("tulsi_edit_items");
        if (raw) {
          const editItems = JSON.parse(raw);
          editItems.forEach((i) => {
            for (let k = 0; k < i.quantity; k++) {
              addItem({ id: i.menu_item_id, name: i.name, price: i.price });
            }
          });
          sessionStorage.removeItem("tulsi_edit_items"); // only rehydrate once
          toast.info("Editing your order — adjust items, then go to checkout");
        }
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    return items.filter((i) => {
      if (category !== "all" && i.category !== category) return false;
      if (vegOnly && !i.is_veg) return false;
      const q = query.toLowerCase();
      if (q && !i.name.toLowerCase().includes(q) && !(i.name_zh || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, category, query, vegOnly]);

  // Group filtered items by category, preserving CATEGORY_IDS order
  const grouped = useMemo(() => {
    const out = [];
    for (const id of CATEGORY_IDS.filter((c) => c !== "all")) {
      const arr = filtered.filter((i) => i.category === id);
      if (arr.length) out.push({ id, items: arr });
    }
    return out;
  }, [filtered]);

  const dispName = (i) => (isZh && i.name_zh) ? i.name_zh : i.name;
  const dispDesc = (i) => (isZh && i.description_zh) ? i.description_zh : i.description;
  const altName = (i) => isZh ? i.name : i.name_zh;

  const handleAdd = (item) => {
    addItem(item);
    toast.success(dispName(item), { description: `NT$ ${item.price}` });
  };

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="menu-page">
      <div className="container-tulsi">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-[10px] tracking-[0.5em] uppercase text-[#A90011] mb-4 font-medium">— {t("menu.subtitle")} —</p>
          <h1 className="font-serif-display text-6xl md:text-8xl text-gold-gradient leading-[1.02]">{t("menu.title")}</h1>
          <div className="gold-separator" />
          <p className="font-serif italic text-lg md:text-xl text-[#2A0708] max-w-xl mx-auto mt-6">{t("menu.intro")}</p>
        </div>

        {/* Controls */}
        <div className="sticky top-20 z-20 -mx-6 md:-mx-12 px-6 md:px-12 py-4 mb-10" style={{ background: "rgba(255, 250, 239, 0.92)", backdropFilter: "blur(12px)", borderBottom: "1px solid #E8D9BE" }}>
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-2 flex-1 px-4 py-2.5 rounded-md border border-[#E8D9BE] bg-white">
              <Search size={16} className="text-[#C9A227]" />
              <input data-testid="menu-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("menu.search")} className="bg-transparent outline-none text-sm text-[#2A0708] flex-1 placeholder:text-[#8E5F65]/60" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-md border border-[#E8D9BE] bg-white" data-testid="veg-only-toggle">
              <input type="checkbox" checked={vegOnly} onChange={(e) => setVegOnly(e.target.checked)} className="accent-[#A90011]" />
              <span className="text-sm text-[#6B4A4E]">🌱 {t("menu.vegOnly")}</span>
            </label>
          </div>
          <div className="flex gap-2 overflow-x-auto pt-3" data-testid="category-tabs">
            {CATEGORY_IDS.map((c) => (
              <button key={c} onClick={() => setCategory(c)} data-testid={`category-${c}`}
                className={`whitespace-nowrap px-5 py-2 rounded-full text-[11px] tracking-[0.18em] uppercase border transition-all ${category === c ? "bg-gradient-to-r from-[#D11D21] to-[#A90011] text-[#FFFAEF] border-transparent shadow-md" : "text-[#6B4A4E] border-[#E8D9BE] bg-white hover:border-[#A90011]/40 hover:text-[#A90011]"}`}>
                {t(`menu.categories.${c}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Sections */}
        {grouped.length === 0 && (
          <p className="text-center text-[#6B4A4E] py-20" data-testid="no-items-msg">{t("menu.noItems")}</p>
        )}

        <div className="space-y-20">
          {grouped.map(({ id, items: catItems }, sectionIdx) => {
            const meta = CAT_META[id];
            return (
              <section key={id} data-testid={`section-${id}`}>
                {/* CHAPTER HEADER */}
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.7 }}
                  className="flex items-end justify-between gap-6 mb-10 pb-4 border-b border-[#E8D9BE]"
                >
                  <div className="flex items-end gap-5">
                    <span className="font-serif-display text-7xl md:text-8xl text-[#C9A227]/30 leading-none select-none">{meta?.num}</span>
                    <div>
                      <p className="text-[10px] tracking-[0.4em] uppercase text-[#A90011] mb-1">{meta?.zh}</p>
                      <h2 className="font-serif-display text-4xl md:text-5xl text-[#2A0708] leading-none">
                        {t(`menu.categories.${id}`)}
                      </h2>
                    </div>
                  </div>
                  <span className="text-4xl md:text-5xl select-none hidden sm:block" aria-hidden="true">{meta?.glyph}</span>
                </motion.div>

                {/* ITEMS GRID */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {catItems.map((item, i) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 18 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-50px" }}
                      transition={{ delay: Math.min(i * 0.04, 0.3) }}
                      whileHover={{ y: -4 }}
                      className="glass-card overflow-hidden flex flex-col group"
                      data-testid={`menu-item-${item.id}`}
                    >
                      {/* Image / placeholder */}
                      <div className="aspect-[5/4] overflow-hidden relative">
                        {item.image_url ? (
                          <img src={item.image_url} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s]" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #FBEBD8, #F4DDC0)" }}>
                            <span className="text-6xl opacity-30 select-none">{CAT_META[item.category]?.glyph || "🍽️"}</span>
                          </div>
                        )}
                        {item.is_special && (
                          <span className="absolute top-3 left-3 text-[9px] tracking-[0.25em] uppercase text-[#FFFAEF] bg-[#A90011] px-2 py-1 rounded">Chef's</span>
                        )}
                      </div>
                      {/* Body */}
                      <div className="p-6 flex-1 flex flex-col">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="min-w-0 flex-1">
                            <h3 className="font-serif-display text-2xl text-[#2A0708] leading-tight">{dispName(item)}</h3>
                            {altName(item) && <p className="text-[11px] uppercase tracking-wider text-[#8E5F65] mt-0.5 truncate">{altName(item)}</p>}
                          </div>
                          <span className="font-serif-display text-xl text-[#A90011] whitespace-nowrap">NT$ {item.price}</span>
                        </div>
                        <p className="text-sm text-[#6B4A4E] mb-5 line-clamp-2 flex-1 leading-relaxed">{dispDesc(item)}</p>
                        <div className="flex items-center justify-between pt-3 border-t border-dashed border-[#E8D9BE]">
                          <div className="flex gap-1.5 items-center">
                            {item.is_veg
                              ? <span className="text-[9px] tracking-widest uppercase text-green-700 border border-green-700/30 px-2 py-0.5 rounded">{t("menu.veg")}</span>
                              : <span className="text-[9px] tracking-widest uppercase text-[#A90011] border border-[#A90011]/30 px-2 py-0.5 rounded">{t("menu.nonVeg")}</span>}
                            {item.is_spicy && <Flame size={13} className="text-[#D11D21]" />}
                            {item.is_popular && <Star size={13} className="text-[#C9A227]" fill="currentColor" />}
                          </div>
                          <button onClick={() => handleAdd(item)} className="btn-saffron text-[10px] px-4 py-2" data-testid={`add-btn-${item.id}`}>
                            <Plus size={12} /> {t("menu.add")}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
